import { Alert, Badge, Button, Modal, Tooltip } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import {
  MdOutlinePayments,
  MdContentCopy,
  MdCheckCircle,
  MdOutlineRefresh,
  MdInfoOutline,
} from "react-icons/md";
import { useForm } from "react-hook-form";
import axios from "../../config/axiosInstance";
import { PAYMENTS } from "../../config/apiConfig";
import { useEffect, useMemo, useState } from "react";
import { useApplication } from "../../contexts/ApplicationContext";
import CurrencyInput from "../inputs/CurrencyInput";

const ModalAddCredit = ({ isOpen, setOpenModal }) => {
  const { user } = useApplication();
  const [qrCode, setQrCode] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [errorPayment, setErrorPayment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const minRecharge = useMemo(
    () => Number(user?.Company?.minRechargePRE || 0),
    [user]
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    control,
    formState: { errors, isSubmitting },
    clearErrors,
    watch,
  } = useForm();

  const valuePix = watch("valuePix");

  const handlePixPayment = async (data) => {
    try {
      const value = Number(data.valuePix || 0);
      if (value < minRecharge) {
        setError("valuePix", {
          type: "manual",
          message: `O valor mínimo permitido é ${minRecharge} reais`,
        });
        return;
      }
      setErrorPayment(null);
      setIsLoading(true);

      const { data: resp } = await axios.get(
        `${PAYMENTS.CREATE}?value=${value}&type=PIX`
      );

      setQrCode(resp?.data?.encodedImage || "");
      setPaymentLink(resp?.data?.payload || "");
      setCopied(false);
    } catch (error) {
      setErrorPayment(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(paymentLink || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // silencioso
    }
  };

  const handleGenerateAgain = () => {
    // permite gerar um novo pagamento mantendo o valor atual
    if (!valuePix || Number(valuePix) < minRecharge) return;
    handlePixPayment({ valuePix });
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    reset();
    setErrorPayment(null);
    setPaymentLink("");
    setQrCode("");
    setCopied(false);
    // define valor mínimo por padrão (opcional)
    if (minRecharge > 0) setValue("valuePix", minRecharge);
  }, [isOpen, reset, setValue, minRecharge]);

  const showForm = !qrCode;
  const showPayment = !!qrCode;

  return (
    <Modal show={isOpen} size="2xl" onClose={handleClose} popup>
      <Modal.Header />
      <Modal.Body>
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-primary/10">
              <MdOutlinePayments className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Adicionar créditos
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Pague via PIX e os créditos serão computados automaticamente.
              </p>
            </div>
          </div>

          {/* Aviso de demora para computar */}
          <Alert color="info" className="border border-blue-200">
            <div className="flex items-start gap-2">
              <MdInfoOutline className="mt-0.5 h-5 w-5 min-w-5" />
              <span className="text-sm">
                Após efetuar o pagamento, o saldo pode levar{" "}
                <strong>alguns minutos</strong> para aparecer na sua conta. Você
                receberá a confirmação dentro do sistema.
              </span>
            </div>
          </Alert>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div
              className={`rounded-xl border p-3 ${
                showPayment ? "border-primary" : "border-gray-200"
              }`}
            >
              <div className="text-xs uppercase tracking-wider text-gray-400">
                Passo 1
              </div>
              <div className="font-medium text-sm">Realize o pagamento</div>
              <p className="text-xs text-gray-500">
                Use o QR Code ou o código.
              </p>
            </div>
            <div className="rounded-xl border p-3 border-gray-200">
              <div className="text-xs uppercase tracking-wider text-gray-400">
                Passo 2
              </div>
              <div className="font-medium text-sm">Confirmação automática</div>
              <p className="text-xs text-gray-500">
                O saldo aparece em alguns minutos.
              </p>
            </div>
          </div>

          {/* Formulário */}
          {showForm && (
            <form
              onSubmit={handleSubmit(handlePixPayment)}
              className="space-y-3"
            >
              <CurrencyInput
                control={control}
                name="valuePix"
                register={register}
                label="Valor da recarga (R$)"
                required
                min={minRecharge}
              />

              {errors.valuePix ? (
                <Alert color="failure" className="p-2">
                  <span className="text-sm">
                    {errors.valuePix?.message ||
                      `Valor mínimo de ${minRecharge} reais.`}
                  </span>
                </Alert>
              ) : (
                <div className="text-xs text-gray-500">
                  Obs: Valor mínimo de {" "}
                  <span className="font-medium text-green-500">{minRecharge} reais</span>.
                </div>
              )}

              <Button
                type="submit"
                className="bg-primary w-full"
                disabled={isLoading || isSubmitting}
                isProcessing={isLoading || isSubmitting}
                processingSpinner={
                  <AiOutlineLoading className="h-6 w-6 animate-spin" />
                }
              >
                Gerar pagamento PIX
              </Button>
            </form>
          )}

          {/* Área de pagamento */}
          {showPayment && (
            <div className="space-y-4">
              <div className="flex flex-col items-center text-center">
                <Badge color="purple" className="mb-2">
                  Pagamento via PIX
                </Badge>

                {/* Valor destacado */}
                {Number(valuePix) > 0 && (
                  <div className="text-sm text-gray-500 mb-1">
                    Valor selecionado
                  </div>
                )}
                {Number(valuePix) > 0 && (
                  <div className="text-2xl font-semibold mb-3">
                    R${" "}
                    {Number(valuePix).toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </div>
                )}

                {/* QR Code */}
                <div className="rounded-2xl border bg-white p-4 shadow-sm dark:bg-gray-900">
                  <div className="text-sm mb-2">Pague com QR Code</div>
                  {qrCode ? (
                    <img
                      className="max-w-56 mx-auto rounded-md"
                      alt="QR Code PIX"
                      src={`data:image/png;base64,${qrCode}`}
                    />
                  ) : (
                    <div className="w-56 h-56 flex items-center justify-center">
                      <AiOutlineLoading className="h-8 w-8 animate-spin" />
                    </div>
                  )}
                </div>

                <div className="py-2">
                  <Badge className="w-fit mx-auto">ou</Badge>
                </div>

                {/* Código PIX (copiar) */}
                <div className="w-full">
                  <div className="text-sm mb-1">Pague com código PIX</div>
                  <div className="flex items-stretch gap-2">
                    <div className="flex-1 rounded-lg border bg-gray-50 dark:bg-gray-900 px-3 py-2 text-left overflow-x-auto text-xs">
                      {paymentLink}
                    </div>
                    <Tooltip content={copied ? "Copiado!" : "Copiar código"}>
                      <Button
                        color={copied ? "success" : "gray"}
                        onClick={handleCopy}
                        className="whitespace-nowrap"
                      >
                        {copied ? (
                          <span className="inline-flex items-center gap-1">
                            <MdCheckCircle className="h-5 w-5" />
                            Copiado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            <MdContentCopy className="h-5 w-5" />
                            Copiar
                          </span>
                        )}
                      </Button>
                    </Tooltip>
                  </div>
                </div>

                {/* Ações rápidas */}
                <div className="flex flex-col sm:flex-row gap-2 w-full mt-3">
                  <Button
                    color="gray"
                    onClick={() => {
                      // permitir novo pagamento com mesmo valor
                      handleGenerateAgain();
                    }}
                    disabled={isLoading}
                  >
                    <span className="inline-flex items-center gap-2">
                      <MdOutlineRefresh className="h-5 w-5" />
                      Gerar novamente
                    </span>
                  </Button>
                  <Button color="gray" onClick={() => setQrCode("")}>
                    Escolher outro valor
                  </Button>
                  <Button className="bg-primary" onClick={handleClose}>
                    Fechar
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Erro */}
          {errorPayment && (
            <Alert color="failure" className="mt-1">
              <span className="font-medium">Ops!</span> Não foi possível gerar a
              cobrança por PIX agora. Tente novamente em instantes.
            </Alert>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ModalAddCredit;
